// ============================================================
// 无糖浏览器 API 封装
// 用户在无糖浏览器内时，通过 nosugar.* Bridge API
// 获取浏览器身份信息，实现免登录跳转
// ============================================================

const TAG = "[无糖]";

// ---------- 配置 ----------
export const NOSUGAR_CONFIG = {
  APP_ID: "BMgraphchain",
};

function utf8ToBase64(value) {
  if (!value || typeof window === "undefined") {
    return "";
  }

  try {
    return window.btoa(unescape(encodeURIComponent(value)));
  } catch (e) {
    console.warn(TAG, "utf8ToBase64 失败:", e);
    return "";
  }
}

function base64ToUtf8(value) {
  if (!value || typeof window === "undefined") {
    return "";
  }

  try {
    return decodeURIComponent(escape(window.atob(value)));
  } catch (e) {
    console.warn(TAG, "base64ToUtf8 失败:", e);
    return "";
  }
}

// ---------- 环境检测 ----------

/** 最长等待 nosugar 对象注入的时间（毫秒）*/
const MAX_WAIT = 5000;
/** 轮询间隔（毫秒）*/
const POLL_INTERVAL = 200;

/**
 * 检测是否运行在无糖浏览器内
 */
export function isNosugarBrowser() {
  let result = false;

  // 1. 打印浏览器指纹信息
  console.log(TAG, "1. 环境检测");
  console.log(TAG, "   UserAgent:", navigator.userAgent);
  console.log(TAG, "   Platform:", navigator.platform);
  console.log(TAG, "   URL:", window.location.href);

  const isWindows = /Win/.test(navigator.platform);
  if (isWindows) {
    console.log(TAG, "   🪟 检测到 Windows 平台");
  }

  // 2. 检查 window.nosugar
  const hasNosugar = typeof window.nosugar !== "undefined";
  console.log(TAG, "   window.nosugar 存在:", hasNosugar);

  if (hasNosugar) {
    console.log(TAG, "   nosugar keys:", Object.keys(window.nosugar));

    // Windows 版本可能 ready 方法不存在或行为不同
    if (typeof window.nosugar.ready === "function") {
      console.log(TAG, "   nosugar.ready 类型:", typeof window.nosugar.ready);
      result = true;
    } else if (window.nosugar.user || window.nosugar.myapp) {
      // Windows 版本可能没有 ready 方法，但有 user 和 myapp API
      console.log(TAG, "   无 ready 方法，但检测到 user/myapp API");
      result = true;
    }
  }

  // 3. 全局扫描，查找任何可能的名字
  const suspects = [];
  for (const key of Object.keys(window)) {
    const lower = key.toLowerCase();
    if (
      lower.includes("sugar") ||
      lower.includes("nosugar") ||
      lower.includes("nosug") ||
      lower.includes("nsg") ||
      lower.includes("wutang") ||
      lower.includes("无糖")
    ) {
      suspects.push(key);
    }
  }
  if (suspects.length > 0) {
    console.log(TAG, "   发现疑似无糖相关对象:", suspects);
  }

  // 4. 尝试直接访问 nosugar（不带 window.前缀的全局变量）
  try {
    const globalNosugar = eval("nosugar");
    if (typeof globalNosugar !== "undefined") {
      console.log(TAG, "   直接 eval('nosugar') 存在:", typeof globalNosugar);
      console.log(TAG, "   keys:", Object.keys(globalNosugar));
      if (
        typeof globalNosugar.ready === "function" ||
        globalNosugar.user ||
        globalNosugar.myapp
      ) {
        // 回填到 window
        window.nosugar = globalNosugar;
        result = true;
      }
    }
  } catch (e) {
    // 不存在
  }

  console.log(
    TAG,
    "   检测结果:",
    result ? "✅ 在无糖浏览器内" : "❌ 未检测到无糖环境",
  );
  return result;
}

/**
 * 等待 nosugar 对象就绪（带轮询重试）
 * 某些版本的浏览器可能在页面加载后才注入 nosugar 对象
 */
export function waitForNosugar() {
  return new Promise((resolve) => {
    const startTime = Date.now();

    function check() {
      if (isNosugarBrowser()) {
        resolve(true);
        return;
      }
      if (Date.now() - startTime >= MAX_WAIT) {
        console.log(TAG, "   等待超时 (" + MAX_WAIT + "ms)，放弃检测");
        resolve(false);
        return;
      }
      console.log(
        TAG,
        "   等待 nosugar 注入中... (" +
          Math.round(Date.now() - startTime) +
          "ms)",
      );
      setTimeout(check, POLL_INTERVAL);
    }

    check();
  });
}

// ---------- API 初始化 ----------

/**
 * 初始化无糖 API
 */
export async function initNosugar() {
  if (!isNosugarBrowser()) {
    throw new Error("Not in Nosugar browser");
  }

  console.log(TAG, "2. 初始化 nosugar.ready()...");

  // Windows 平台兼容处理
  const isWindows = /Win/.test(navigator.platform);
  console.log(TAG, "   检测平台:", isWindows ? "Windows" : "其他");

  try {
    // 检查 ready 方法是否存在
    if (typeof window.nosugar.ready === "function") {
      await window.nosugar.ready();
      console.log(TAG, "   nosugar.ready() ✅ 初始化完成");
    } else {
      console.log(
        TAG,
        "   nosugar.ready 不存在，跳过初始化（某些版本无需显式调用）",
      );
    }
  } catch (e) {
    // Windows 版本可能在 ready() 调用时报错，但实际上 API 已可用
    console.warn(TAG, "   nosugar.ready() 调用失败，但继续尝试使用 API:", e);

    // 验证关键 API 是否可用
    const hasUserAPI =
      window.nosugar.user &&
      typeof window.nosugar.user.isLogined === "function";
    const hasMyappAPI =
      window.nosugar.myapp &&
      typeof window.nosugar.myapp.getAccessToken === "function";

    console.log(TAG, "   user API 可用:", hasUserAPI);
    console.log(TAG, "   myapp API 可用:", hasMyappAPI);

    if (!hasUserAPI || !hasMyappAPI) {
      throw new Error("关键 API 不可用: " + e.message);
    }

    console.log(TAG, "   ⚠️ ready() 失败但 API 可用，继续执行");
  }

  return window.nosugar;
}

// ---------- 用户相关 ----------

/**
 * 检查浏览器用户是否已登录
 */
export async function checkNosugarLogin() {
  console.log(TAG, "3. 检查浏览器登录状态...");
  const ns = await initNosugar();

  try {
    const result = await ns.user.isLogined();
    console.log(TAG, "   isLogined() 返回:", result);
    console.log(
      TAG,
      "   ",
      result ? "✅ 浏览器已登录" : "⚠️ 浏览器未登录，需要唤起登录",
    );
    return result;
  } catch (e) {
    console.error(TAG, "   isLogined() 调用失败:", e);
    // Windows 平台如果该方法不可用，尝试其他判断方式
    const isWindows = /Win/.test(navigator.platform);
    if (isWindows) {
      console.log(TAG, "   [Windows] 尝试通过 getAccessToken 判断登录状态");
      try {
        const token = await ns.myapp.getAccessToken(NOSUGAR_CONFIG.APP_ID);
        const isLoggedIn = !!token;
        console.log(
          TAG,
          "   通过 token 判断:",
          isLoggedIn ? "✅ 已登录" : "⚠️ 未登录",
        );
        return isLoggedIn;
      } catch (tokenError) {
        console.log(TAG, "   token 获取失败，判断为未登录");
        return false;
      }
    }
    throw e;
  }
}

/**
 * 唤起浏览器登录页
 */
export async function triggerNosugarLogin(force = false) {
  console.log(TAG, "4a. 唤起浏览器登录窗口 (force=" + force + ")...");
  const ns = await initNosugar();

  try {
    await ns.myapp.login(force);
    console.log(TAG, "   login() 调用完成，等待用户操作...");
  } catch (e) {
    console.error(TAG, "   login() 调用失败:", e);
    const isWindows = /Win/.test(navigator.platform);
    if (isWindows) {
      console.error(TAG, "   [Windows] 登录方法可能不可用，请检查浏览器版本");
    }
    throw e;
  }
}

/**
 * 获取 access_token
 */
export async function getNosugarAccessToken(appId) {
  console.log(TAG, "5. 获取 access_token (appId=" + appId + ")...");

  const isWindows = /Win/.test(navigator.platform);
  console.log(TAG, "   当前平台:", isWindows ? "Windows" : "其他");

  const ns = await initNosugar();

  // 诊断：先看看浏览器里有哪些应用
  try {
    const appName = await ns.myapp.getName();
    console.log(TAG, "   当前应用名:", appName);
  } catch (e) {
    console.log(TAG, "   getName() 不可用:", e.message);
  }

  try {
    const args = await ns.myapp.getArgs();
    console.log(TAG, "   外部传入参数 getArgs():", args);
  } catch (e) {
    console.log(TAG, "   getArgs() 不可用:", e.message);
  }

  try {
    const version = await ns.myapp.getBrowserVersion();
    console.log(TAG, "   浏览器版本:", version);
  } catch (e) {
    console.log(TAG, "   getBrowserVersion() 不可用:", e.message);
  }

  // Windows 平台特殊处理：检查 myapp 对象的可用方法
  if (isWindows) {
    console.log(
      TAG,
      "   [Windows] 可用的 myapp 方法:",
      Object.keys(ns.myapp || {}),
    );
  }

  // 尝试调用 getAccessToken
  try {
    console.log(TAG, "   调用 getAccessToken('" + appId + "')...");
    const result = await ns.myapp.getAccessToken(appId);
    console.log(TAG, "   ✅ getAccessToken 成功返回类型:", typeof result);
    console.log(TAG, "   返回内容:", result);

    if (typeof result === "string") return result;
    if (result && result.access_token) return result.access_token;

    // Windows 平台可能返回格式不同
    if (result && typeof result === "object") {
      console.log(TAG, "   返回对象的键:", Object.keys(result));
    }

    return result;
  } catch (e) {
    console.error(TAG, "   ❌ getAccessToken('" + appId + "') 失败:", e);
    console.error(TAG, "   错误类型:", e.constructor.name);
    console.error(TAG, "   错误消息:", e.message);
    console.error(TAG, "   请确认：1) 此 app_id 是否已在无糖浏览器后台注册");
    console.error(TAG, "          2) 无糖浏览器是否已配置图链的应用入口");
    console.error(TAG, "          3) 当前 asar 是否已正确替换");

    if (isWindows) {
      console.error(
        TAG,
        "   [Windows] 请检查无糖浏览器 Windows 版本是否支持此 API",
      );
    }

    throw e;
  }
}

// ---------- 用户信息 ----------

/**
 * 获取登录用户基本信息
 * 对应 nosugar.user.info()
 * @returns {Promise<{id, username, province, city, token, ...}>}
 */
export async function getNosugarUserInfo() {
  console.log(TAG, "获取用户基本信息...");
  const ns = await initNosugar();
  const result = await ns.user.info();
  console.log(TAG, "   user.info() 返回:", result);
  return result;
}

/**
 * 获取登录用户完整信息（含用户类别 kind）
 * 对应 nosugar.user.getFullUserInfo()
 * @returns {Promise<{id, username, created_at, kind, ...}>}
 */
export async function getNosugarFullUserInfo() {
  console.log(TAG, "获取用户完整信息...");
  const ns = await initNosugar();
  const result = await ns.user.getFullUserInfo();
  console.log(TAG, "   user.getFullUserInfo() 返回:", result);
  return result;
}

// ---------- 工具方法 ----------

/**
 * 打开网址（无糖浏览器内）
 * 对应 nosugar.myapp.openUrl(url, target)
 */
export async function nosugarOpenUrl(url, target) {
  console.log(TAG, "打开网址:", url);
  console.log(TAG, "  目标:", target);
  console.log(TAG, "  平台:", navigator.platform);

  const ns = await initNosugar();

  // 检测是否为Windows平台
  const isWindows = /Win/.test(navigator.platform);
  console.log(TAG, "  检测到Windows环境:", isWindows);

  try {
    const result = await ns.myapp.openUrl(url, target);
    console.log(TAG, "  openUrl 返回:", result);
    return result;
  } catch (e) {
    console.error(TAG, "  openUrl 失败:", e);
    throw e;
  }
}

/**
 * 启动其他无糖应用
 * 对应 nosugar.app.start(appid, args)
 * @returns {Promise}
 */
export async function nosugarStartApp(appid, args) {
  console.log(TAG, "启动应用:", appid);
  const ns = await initNosugar();
  return ns.app.start(appid, args);
}

export function encodeNosugarPath(pathPlain) {
  return utf8ToBase64(pathPlain);
}

export function decodeNosugarPath(pathParam) {
  return base64ToUtf8(pathParam);
}
